import type {
  IAgentAutoPausedNotificationData,
  IDashboardAccessRevokedNotificationData,
  IDashboardPermissionChangedNotificationData,
  IDashboardSharedNotificationData,
  IMeetingFailedNotificationData,
  IUsageThresholdReachedNotificationData,
  IWebhookRunResultNotificationData,
  TRunFailedNotification,
  TSkillNotification,
} from "@frontend-shared/types";
import type { TFunction } from "i18next";

import type { IViewDashboardProps, IViewSkillProps } from "../types";

// NOTE: Failure status values emitted by the meeting workflow callback. The
// server payload (`IMeetingFailedNotificationData.failureStatus`) is typed as
// a free `string` to stay forward-compatible — this enum mirrors the
// presently-recognised values so the card has exhaustive switch coverage.
export enum EMeetingFailureStatus {
  RECORDING_FAILED = "recording_failed",
  TRANSCRIPTION_FAILED = "transcription_failed",
  SUMMARIZATION_FAILED = "summarization_failed",
  NO_AUDIO_TRACK = "no_audio_track",
  BOT_NOT_ADMITTED = "bot_not_admitted",
  BOT_LOBBY_TIMEOUT = "bot_lobby_timeout",
}

export interface IMeetingFailedCardProps {
  uuid: string;
  data: IMeetingFailedNotificationData;
  createdAt: string;
  isRead: boolean;
  onViewMeeting: (meetingUuid: string) => void;
}

export interface IUsageThresholdReachedCardProps {
  uuid: string;
  data: IUsageThresholdReachedNotificationData;
  createdAt: string;
  isRead: boolean;
  isTokenUsageEnabled: boolean;
  onViewUsageLimits: (organizationUuid: string) => void;
  onViewUsage: () => void;
}

export interface IWebhookRunResultCardProps {
  uuid: string;
  data: IWebhookRunResultNotificationData;
  createdAt: string;
  isRead: boolean;
  isSuccess: boolean;
  onViewRuns: (automationUuid: string, projectUuid?: string) => void;
}

export interface IAgentAutoPausedCardProps {
  uuid: string;
  data: IAgentAutoPausedNotificationData;
  createdAt: string;
  isRead: boolean;
  onViewRuns: (automationUuid: string, projectUuid?: string) => void;
}

export interface IRunFailedCardProps {
  uuid: string;
  notification: TRunFailedNotification;
  createdAt: string;
  isRead: boolean;
  onViewRuns: (automationUuid: string, projectUuid?: string) => void;
}

export interface IResolveBodyParams {
  t: TFunction<"notifications">;
  failureStatus: string;
  meetingTitle: string;
}

export interface IDashboardSharedCardProps {
  uuid: string;
  data: IDashboardSharedNotificationData;
  createdAt: string;
  isRead: boolean;
  onViewDashboard: (props: IViewDashboardProps) => void;
}

export interface IDashboardPermissionChangedCardProps {
  uuid: string;
  data: IDashboardPermissionChangedNotificationData;
  createdAt: string;
  isRead: boolean;
  onViewDashboard: (props: IViewDashboardProps) => void;
}

export interface IDashboardAccessRevokedCardProps {
  uuid: string;
  data: IDashboardAccessRevokedNotificationData;
  createdAt: string;
  isRead: boolean;
}

export interface ISkillNotificationCardProps {
  uuid: string;
  notification: TSkillNotification;
  createdAt: string;
  isRead: boolean;
  onViewSkill: (props: IViewSkillProps) => void;
}
